import { Params } from '@angular/router';
import {
    CentralRegulatorDTO,
    CompanyDTO,
    OfficeDTO,
    ProductAgreementDTO,
    ProductDTO,
    SubProductDTO
} from '@iota/iota-management-portal-v4-client';
import { getRouterSelectors, routerReducer, RouterReducerState, RouterState } from '@ngrx/router-store';
import { ActionReducer, ActionReducerMap, createFeatureSelector, createSelector, MetaReducer } from '@ngrx/store';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import { CategoryFilterTab } from '../shared/components/category-filter-tabs/category-filter-tabs.component';
import { IotaConfigAttributeScope } from '../shared/constants-and-enums/iota-config';
import { RouteData } from '../shared/constants-and-enums/route-data';
import {
    SMART_EMPLOYEE_OVERVIEW_FILTER,
    REGISTERED_EMPLOYEE_OVERVIEW_FILTER,
    SmartEmployeeOverviewFilterLabels,
    RegisteredEmployeeOverviewFilterLabels
} from '../shared/constants-and-enums/smart-employee-overview-types';
import {
    getAttributeFirstValue,
    getAttributeValueInDefaultLanguage,
    getActiveOffices,
    sortByNameWithAlphabeticCharactersFirst,
    sortOfficesByDeactivationStatus
} from '../shared/utils';
import { AgreementEffects } from './effects/agreement.effects';
import { BreadcrumbEffects } from './effects/breadcrumb.effects';
import { CompanyEmployeeEffects } from './effects/company-employee.effects';
import { RegisteredEmployeeEffects } from './effects/registered-employee.effects';
import { CompanyProductSelectionEffects } from './effects/company-product-selection.effects';
import { CompanySelectionDescriptionEffects } from './effects/company-selection-description.effect';
import { CompanyEffects } from './effects/company.effects';
import { CompanyDeactivationEffects } from './effects/company-deactivation.effects';
import { CentralRegulatorDeactivationEffects } from './effects/central-regulator-deactivation.effects';
import { ConfigEffects } from './effects/config.effects';
import { DocumentEffects } from './effects/document.effects';
import { EmployeeCaseEffects } from './effects/employee-case.effects';
import { EmployeeSubscriptionEffects } from './effects/employee-subscription.effects';
import { EmployeeTransactionEffects } from './effects/employee-transaction.effects';
import { EmployeeEffects } from './effects/employee.effects';
import { IotaConfigEffects } from './effects/iota-config.effects';
import { LanguageEffects } from './effects/language.effects';
import { NotificationEffects } from './effects/notification.effects';
import { OfficeEffects } from './effects/office.effects';
import { PersonEffects } from './effects/person.effects';
import { ProductEffects } from './effects/product.effects';
import { RegistrationPeriodEffects } from './effects/registration-period.effects';
import { ReportingEffects } from './effects/reporting.effects';
import { SmartDocumentUploadEffects } from './effects/smart-document-upload.effects';
import { UserEffects } from './effects/user.effects';
import {
    buildAuditInvoiceSummaryEntities,
    buildDocumentDescriptionEntities,
    buildPayrollReportSummaryEntities,
    buildPlatformLicenseActivationWithLicenseEntities,
    buildPlatformLicenseEntities
} from './entity-builder-utils';
import { CompanyProductSelectionWithMeta, CompanyRegistrationPeriodEntity, ProductEntity, SubProductEntity } from './entity-models';
import { AsyncState } from '../shared/utils/async-state.util';
import { agreementReducer, AgreementState, allAgreements } from './reducer/agreement.reducer';
import { breadcrumbReducer, BreadcrumbState } from './reducer/breadcrumb.reducer';
import { companyEmployeeFilterCounts, companyEmployeeReducer, CompanyEmployeeState } from './reducer/company-employee.reducer';
import { EmployeeFilterCounts } from './actions/company-employee.actions';
import { registeredEmployeeCounts, registeredEmployeeReducer, RegisteredEmployeeState } from './reducer/registered-employee.reducer';
import {
    allCompanyProductSelections,
    companyProductSelectionReducer,
    CompanyProductSelectionState
} from './reducer/company-product-selection.reducer';
import {
    allCompanySelectionDescriptions,
    companySelectionDescriptionReducer,
    CompanySelectionDescriptionState
} from './reducer/company-selection-description.reducer';
import {
    companies,
    companyIdToCompanyNumberMap,
    companyIdToNameMap,
    companyReducer,
    CompanyState,
    currentCompany,
    licenseActivationIdsOfCompanyThatHaveNotBeenInvoiced
} from './reducer/company.reducer';
import { configReducer, ConfigState } from './reducer/config.reducer';
import { documentReducer, DocumentState } from './reducer/document.reducer';
import { employeeCaseByIdLoading, employeeCaseReducer, employeeCases, EmployeeCaseState } from './reducer/employee-case.reducer';
import { employeeSubscriptionReducer, EmployeeSubscriptionState } from './reducer/employee-subscription.reducer';
import { employeeTransactionReducer, EmployeeTransactionState } from './reducer/employee-transaction.reducer';
import { employeeReducer, EmployeeState } from './reducer/employee.reducer';
import { iotaConfigReducer, IotaConfigState } from './reducer/iota-config.reducer';
import { languageReducer, LanguageState } from './reducer/language.reducer';
import { notificationReducer, NotificationState } from './reducer/notification.reducer';
import { allOffices, officeReducer, OfficeState } from './reducer/office.reducer';
import { personReducer, PersonState } from './reducer/person.reducer';
import { allFullProducts, fullProductsLoading, productReducer, ProductState, requestedFullProductId } from './reducer/product.reducer';
import {
    allRegistrationPeriods,
    registrationPeriodIdsByProduct,
    registrationPeriodReducer,
    RegistrationPeriodState
} from './reducer/registration-period.reducer';
import { reportingReducer, ReportingState } from './reducer/reporting.reducer';
import { smartDocumentUploadReducer, SmartDocumentUploadState } from './reducer/smart-document-upload.reducer';
import { userReducer, UserState } from './reducer/user.reducer';
import {
    _platformLicenses,
    platformLicenseActivationsByPlatformLicense,
    platformLicensePricingIdsThatHaveNotBeenInvoiced,
    platformLicenseReducer,
    PlatformLicenseState
} from './reducer/platform-license.reducer';
import { PlatformLicenseEffects } from './effects/platform-license.effects';
import { ProductAttribute } from '../shared/constants-and-enums/attribute-types';
import { termsOfPaymentReducer, TermsOfPaymentState } from './reducer/terms-of-payment.reducer';
import { TermsOfPaymentEffects } from './effects/terms-of-payment.effects';
import { HwpState, hwpSurveyReducer } from './reducer/hwp-survey.reducer';
import { HwpSurveyEffects } from './effects/hwp-survey.effects';
import { invoiceCreationProcessReducer, InvoiceCreationProcessState } from './reducer/invoice-creation-process.reducer';
import { InvoiceCreationProcessEffects } from './effects/invoice-creation-process.effects';
import { _auditInvoiceSummaries, auditInvoiceSummaryReducer, AuditInvoiceSummaryState } from './reducer/audit-invoice-summary.reducer';
import { AuditInvoiceSummaryEffects } from './effects/audit-invoice-summary.effects';
import { CentralRegulatorsEffects } from './effects/central-regulators.effects';
import {
    allCentralRegulators,
    centralRegulatorIdToCentralRegulatorNumberMap,
    centralRegulatorIdToNameMap,
    centralRegulatorReducer,
    CentralRegulatorState,
    licenseActivationIdsOfCentralRegulatorThatHaveNotBeenInvoiced,
    loadedCentralRegulator
} from './reducer/central-regulators.reducer';
import {
    allEmployeeGroupChanges,
    employeeGroupChangeCompanyInformation,
    employeeGroupChangeReducer,
    EmployeeGroupChangeState
} from './reducer/employee-group-change.reducer';
import { EmployeeGroupChangeEffects } from './effects/employee-group-change.effects';
import { _payrollReportSummaries, payrollReportSummaryReducer, PayrollReportSummaryState } from './reducer/payroll-report-summary.reducer';
import { PayrollReportSummaryEffects } from './effects/payroll-report-summary.effects';
import { payrollReportCreationProcessReducer, PayrollReportCreationProcessState } from './reducer/payroll-report-creation-process.reducer';
import { PayrollReportCreationProcessEffects } from './effects/payroll-report-creation-process.effects';
import { allEmployeeLeavingRequests, employeeLeavingReducer, EmployeeLeavingState } from './reducer/employee-leaving.reducer';
import { EmployeeLeavingEffects } from './effects/employee-leaving.effects';
import { B2CEmployeeEffects } from './effects/b2c-employee.effects';
import { b2cEmployeeReducer, B2CEmployeeState } from './reducer/b2c-employee.reducer';
import { CustomerSegmentationEffects } from './effects/customer-segmentation.effects';
import { customerSegmentationReducer, CustomerSegmentationState } from './reducer/customer-segmentation.reducer';
import { processEventLogReducer, ProcessEventLogState } from './reducer/process-event-log.reducer';
import { ProcessEventLogEffects } from './effects/process-event-log.effects';

export interface AppState {
    router: RouterState; // needs to be 'router' exactly, else the selectors don't work
    agreementState: AgreementState;
    userState: UserState;
    companyEmployeeState: CompanyEmployeeState;
    registeredEmployeeState: RegisteredEmployeeState;
    companyState: CompanyState;
    companyProductSelectionState: CompanyProductSelectionState;
    configState: ConfigState;
    documentState: DocumentState;
    employeeCaseState: EmployeeCaseState;
    officeState: OfficeState;
    employeeState: EmployeeState;
    personState: PersonState;
    productState: ProductState;
    platformLicenseState: PlatformLicenseState;
    registrationPeriodState: RegistrationPeriodState;
    reportingState: ReportingState;
    notificationState: NotificationState;
    employeeTransactionState: EmployeeTransactionState;
    breadcrumbState: BreadcrumbState;
    employeeSubscriptionState: EmployeeSubscriptionState;
    companySelectionDescriptionState: CompanySelectionDescriptionState;
    iotaConfigState: IotaConfigState;
    languageState: LanguageState;
    smartDocumentUploadState: SmartDocumentUploadState;
    termsOfPaymentState: TermsOfPaymentState;
    hwpState: HwpState;
    invoiceCreationProcessState: InvoiceCreationProcessState;
    auditInvoiceSummaryState: AuditInvoiceSummaryState;
    centralRegulatorState: CentralRegulatorState;
    employeeGroupChangeState: EmployeeGroupChangeState;
    payrollReportSummaryState: PayrollReportSummaryState;
    payrollReportCreationProcessState: PayrollReportCreationProcessState;
    employeeLeavingState: EmployeeLeavingState;
    b2cEmployeeState: B2CEmployeeState;
    customerSegmentation: CustomerSegmentationState;
    processEventLogState: ProcessEventLogState;
}

export const reducers: ActionReducerMap<AppState> = {
    router: routerReducer,
    agreementState: agreementReducer,
    userState: userReducer,
    companyEmployeeState: companyEmployeeReducer,
    registeredEmployeeState: registeredEmployeeReducer,
    companyState: companyReducer,
    companyProductSelectionState: companyProductSelectionReducer,
    configState: configReducer,
    documentState: documentReducer,
    employeeCaseState: employeeCaseReducer,
    officeState: officeReducer,
    employeeState: employeeReducer,
    personState: personReducer,
    productState: productReducer,
    platformLicenseState: platformLicenseReducer,
    registrationPeriodState: registrationPeriodReducer,
    reportingState: reportingReducer,
    notificationState: notificationReducer,
    employeeTransactionState: employeeTransactionReducer,
    breadcrumbState: breadcrumbReducer,
    employeeSubscriptionState: employeeSubscriptionReducer,
    companySelectionDescriptionState: companySelectionDescriptionReducer,
    iotaConfigState: iotaConfigReducer,
    languageState: languageReducer,
    smartDocumentUploadState: smartDocumentUploadReducer,
    termsOfPaymentState: termsOfPaymentReducer,
    hwpState: hwpSurveyReducer,
    invoiceCreationProcessState: invoiceCreationProcessReducer,
    auditInvoiceSummaryState: auditInvoiceSummaryReducer,
    centralRegulatorState: centralRegulatorReducer,
    employeeGroupChangeState: employeeGroupChangeReducer,
    payrollReportSummaryState: payrollReportSummaryReducer,
    payrollReportCreationProcessState: payrollReportCreationProcessReducer,
    employeeLeavingState: employeeLeavingReducer,
    b2cEmployeeState: b2cEmployeeReducer,
    customerSegmentation: customerSegmentationReducer,
    processEventLogState: processEventLogReducer
};

export function debug(reducer: ActionReducer<any>): ActionReducer<any> {
    return function (state, action) {
        if (environment.enableLogging) {
            console.log('state', state);
            console.log('action', action);
        }
        return reducer(state, action);
    };
}

export const metaReducers: MetaReducer<any>[] = [debug];

export const effects = [
    AgreementEffects,
    UserEffects,
    CompanyEmployeeEffects,
    RegisteredEmployeeEffects,
    CompanyEffects,
    CompanyDeactivationEffects,
    CentralRegulatorDeactivationEffects,
    CompanyProductSelectionEffects,
    ConfigEffects,
    DocumentEffects,
    EmployeeCaseEffects,
    OfficeEffects,
    EmployeeEffects,
    PersonEffects,
    ProductEffects,
    PlatformLicenseEffects,
    RegistrationPeriodEffects,
    ReportingEffects,
    NotificationEffects,
    EmployeeTransactionEffects,
    BreadcrumbEffects,
    EmployeeSubscriptionEffects,
    CompanySelectionDescriptionEffects,
    IotaConfigEffects,
    LanguageEffects,
    SmartDocumentUploadEffects,
    TermsOfPaymentEffects,
    HwpSurveyEffects,
    InvoiceCreationProcessEffects,
    AuditInvoiceSummaryEffects,
    CentralRegulatorsEffects,
    EmployeeGroupChangeEffects,
    PayrollReportSummaryEffects,
    PayrollReportCreationProcessEffects,
    EmployeeLeavingEffects,
    B2CEmployeeEffects,
    CustomerSegmentationEffects,
    ProcessEventLogEffects
];

export const selectRouter = createFeatureSelector<RouterReducerState<any>>('router');

export const {
    selectCurrentRoute, // select the current route
    selectFragment, // select the current route fragment
    selectQueryParams, // select the current route query params
    selectQueryParam, // factory function to select a query param
    selectRouteParams, // select the current route params
    selectRouteParam, // factory function to select a route param
    selectRouteData, // select the current route data
    selectUrl // select the current url
} = getRouterSelectors();

export const selectAllRouteParams = createSelector(selectRouter, (router) => {
    let currentRoute = router?.state?.root;
    let params: Params = {};
    while (currentRoute?.firstChild) {
        currentRoute = currentRoute.firstChild;
        params = {
            ...params,
            ...currentRoute.params
        };
    }
    return params;
});

export const transparentBackground = createSelector(selectRouteData, (data: RouteData) => data?.transparentBackground === true);
export const selectedEmployeeId = createSelector(selectAllRouteParams, ({ employeeId }) => employeeId);

export const selectedCompanyId = createSelector(selectAllRouteParams, ({ companyId }) => companyId);
export const selectedCompany = createSelector(currentCompany, selectedCompanyId, (company, id) =>
    company?.companyId === id ? company : undefined
);
export const selectedRegulatorId = createSelector(selectAllRouteParams, ({ regulatorId }) => regulatorId);

// Reads from the dedicated loadedRegulator slice (populated by ReloadRegulator/UpsertRegulator on
// the details page), not the entity adapter. The adapter holds the stripped list response and is
// refreshed on app init; using it here would cause that response to clobber the full DTO mid-flight.
export const selectedRegulator = createSelector(loadedCentralRegulator, selectedRegulatorId, (loaded, regulatorId) =>
    loaded?.centralRegulatorId === regulatorId ? loaded : undefined
);

export const companiesBySelectedRegulator = createSelector(companies, selectedRegulatorId, (companies, selectedRegulatorId) => {
    return companies.filter((company) => company.centralRegulatorId === selectedRegulatorId);
});

export const companiesByRegulatorId = (regulatorId: string) =>
    createSelector(companies, (companies) => {
        return companies.filter((company) => company.centralRegulatorId === regulatorId);
    });

export const selectedRegulatorAddresses = createSelector(selectedRegulator, (regulator: CentralRegulatorDTO) => regulator?.addresses ?? []);

export const selectedRegistrationPeriodId = createSelector(
    selectAllRouteParams,
    ({ companyRegistrationPeriodId }) => companyRegistrationPeriodId
);
export const selectedRegistrationPeriod = createSelector(
    allRegistrationPeriods,
    selectedRegistrationPeriodId,
    (registrationPeriods, companyRegistrationPeriodId) =>
        registrationPeriods?.find((r) => r.companyRegistrationPeriodId === companyRegistrationPeriodId)
);

export const selectedCaseId = createSelector(selectAllRouteParams, ({ caseId }) => caseId as string);
export const selectedCase = createSelector(employeeCases, selectedCaseId, selectedEmployeeId, (employeeCases, caseId, employeeId) =>
    employeeCases.find((employeeCase) => employeeCase.employeeCaseId === caseId && employeeCase.employeeId === employeeId)
);
export const selectedCaseLoading = createSelector(
    selectedCaseId,
    selectedCase,
    employeeCaseByIdLoading,
    (caseId, employeeCase, loading) => !!caseId && !employeeCase && loading
);

export const officesBySelectedCompany = createSelector(selectedCompany, allOffices, (company: CompanyDTO, offices: OfficeDTO[]) => {
    if (company && offices) {
        return offices
            .filter((office) => office.companyId === company?.companyId)
            .sort((a, b) => moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime());
    }

    return [];
});
export const officesForSelectionBySelectedCompany = createSelector(officesBySelectedCompany, (offices: OfficeDTO[]) =>
    sortOfficesByDeactivationStatus(sortByNameWithAlphabeticCharactersFirst(offices))
);
// Deactivated offices must not be assignable through the smart assistants, so they are hidden from every
// office column hint and never used as an implicit single-office default.
export const activeOfficesBySelectedCompany = createSelector(officesBySelectedCompany, (offices: OfficeDTO[]) => getActiveOffices(offices));
export const selectedCompanyOfficeIdToNameMap = createSelector(officesBySelectedCompany, (offices: OfficeDTO[]) => {
    return new Map(offices?.map((office) => [office.officeId, office.name]));
});

export const registrationPeriodsBySelectedCompany = createSelector(
    selectedCompany,
    allRegistrationPeriods,
    (company: CompanyDTO, registrationPeriods: CompanyRegistrationPeriodEntity[]) => {
        if (company && registrationPeriods) {
            return registrationPeriods.filter((elem) => elem.companyId === company?.companyId);
        }

        return undefined;
    }
);

export const selectedCompanyAddresses = createSelector(selectedCompany, (company: CompanyDTO) => company?.addresses ?? []);
export const documentsBySelectedCompany = createSelector(selectedCompany, officesBySelectedCompany, (company, offices) =>
    buildDocumentDescriptionEntities(company?.attachments ?? [], offices)
);

export const documentsBySelectedRegulator = createSelector(selectedRegulator, (regulator: CentralRegulatorDTO) =>
    buildDocumentDescriptionEntities(regulator?.attachments ?? [], [])
);

export const documentsBySelectedBusinessPartner = createSelector(
    selectedCompany,
    selectedRegulator,
    documentsBySelectedCompany,
    documentsBySelectedRegulator,
    (company, regulator, companyDocuments, regulatorDocuments) => {
        if (company) {
            return companyDocuments;
        } else if (regulator) {
            return regulatorDocuments;
        } else {
            return [];
        }
    }
);

export const notesBySelectedBusinessPartner = createSelector(selectedCompany, selectedRegulator, (company, regulator) => {
    if (company) {
        return company.notes;
    } else if (regulator) {
        return regulator.notes;
    } else {
        return [];
    }
});

export const selectionKeysBySelectedCompany = createSelector(selectedCompany, allCompanySelectionDescriptions, (company, selections) =>
    selections?.filter((elem) => elem?.companyId === company?.companyId)?.map((selection) => selection?.selectionKey)
);

export const selectedCompanyEmployeeGroupChangeInformation = createSelector(
    selectedCompany,
    employeeGroupChangeCompanyInformation,
    (company, informationByCompany) => informationByCompany?.[company?.companyId]
);
export const employeeGroupChangeRequestsBySelectedCompany = createSelector(
    selectedCompany,
    allEmployeeGroupChanges,
    (company, groupChanges) => groupChanges?.filter((groupChange) => groupChange.companyId === company?.companyId) ?? []
);

export const employeeLeavingRequestsBySelectedCompany = createSelector(
    selectedCompany,
    allEmployeeLeavingRequests,
    (company, leavingRequests) => leavingRequests?.filter((leavingRequest) => leavingRequest.companyId === company?.companyId) ?? []
);

export const fullProductById = (id: string) =>
    createSelector(allFullProducts, (fullProducts) => fullProducts.find((product) => id === product.productId));

export const selectedProductId = createSelector(selectAllRouteParams, ({ productId }) => productId);
export const selectedProduct = createSelector(allFullProducts, selectedProductId, (fullProducts, productId) => {
    const found = fullProducts.find((product) => product.productId === productId);

    if (found) {
        return found;
    } else {
        return fullProducts.flatMap((product) => product.subProducts ?? []).find((sub) => sub?.subProductId === productId);
    }
});
export const selectedProductIsSubProduct = createSelector(selectedProduct, (product) => {
    return !!(product as SubProductDTO)?.subProductId;
});
export const selectedProductAttributeScope = createSelector(
    selectedProductIsSubProduct,
    (isSubProduct): IotaConfigAttributeScope => (isSubProduct ? 'SUB_PRODUCT' : 'PRODUCT')
);
export const selectedProductCategory = createSelector(selectedProduct, (product) => (product as ProductDTO)?.productCategory);

/** For a product that is not a sub product this is the product itself. */
export const selectedParentProduct = createSelector(allFullProducts, selectedProduct, (fullProducts, product) =>
    fullProducts.find((fullProduct) => fullProduct.productId === product?.productId)
);

/** An id that a finished request did not resolve is an error, not an endless skeleton. */
export const selectedProductState = createSelector(
    selectedProduct,
    selectedProductId,
    requestedFullProductId,
    fullProductsLoading,
    (product, productId, requestedProductId, loading): AsyncState<ProductEntity | SubProductEntity> => {
        if (product) {
            return { status: 'ok', data: product };
        }

        return requestedProductId === productId && !loading ? { status: 'error' } : { status: 'loading' };
    }
);

export const agreementsBySelectedProduct = createSelector(
    selectedProduct,
    allAgreements,
    (product: ProductDTO | SubProductDTO, agreements: ProductAgreementDTO[]) => {
        let id = product?.productId;

        if ((product as SubProductDTO)?.subProductId) {
            id = (product as SubProductDTO).subProductId;
        }

        if (product && agreements) {
            return agreements.filter((agreement) => agreement.productId === id);
        }

        return [];
    }
);
export const registrationPeriodsBySelectedProduct = createSelector(
    selectedProduct,
    registrationPeriodIdsByProduct,
    allRegistrationPeriods,
    (product, periodIdsByProduct, registrationPeriods) => {
        const registrationPeriodIds = periodIdsByProduct?.[product?.productId] ?? [];

        return registrationPeriods?.filter((period) => registrationPeriodIds.includes(period.companyRegistrationPeriodId));
    }
);

export const smartEmployeeOverviewFilterTabsBySelectedCompany = createSelector(
    companyEmployeeFilterCounts,
    (filterCounts: EmployeeFilterCounts | null) => {
        const enumKeyAndValues = Object.values(SMART_EMPLOYEE_OVERVIEW_FILTER);
        const enumValues = enumKeyAndValues.filter((value) => isNaN(Number(value)));

        return enumValues?.map((key) => {
            let count = 0;
            if (filterCounts) {
                switch (key) {
                    case SMART_EMPLOYEE_OVERVIEW_FILTER.ALL:
                        count = filterCounts.all;
                        break;
                    case SMART_EMPLOYEE_OVERVIEW_FILTER.REGISTERED:
                        count = filterCounts.registered;
                        break;
                    case SMART_EMPLOYEE_OVERVIEW_FILTER.NOT_REGISTERED:
                        count = filterCounts.notRegistered;
                        break;
                    case SMART_EMPLOYEE_OVERVIEW_FILTER.FORMER:
                        count = filterCounts.former;
                        break;
                }
            }

            const result: CategoryFilterTab = {
                key,
                label: SmartEmployeeOverviewFilterLabels?.[key],
                count
            };

            return result;
        });
    }
);

export const registeredEmployeeFilterTabs = createSelector(registeredEmployeeCounts, (counts) => {
    const enumValues = Object.values(REGISTERED_EMPLOYEE_OVERVIEW_FILTER);

    return enumValues.map((key) => {
        let count = 0;
        if (counts) {
            switch (key) {
                case REGISTERED_EMPLOYEE_OVERVIEW_FILTER.REGISTERED:
                    count = counts.active;
                    break;
                case REGISTERED_EMPLOYEE_OVERVIEW_FILTER.FORMER:
                    count = counts.former;
                    break;
            }
        }

        const result: CategoryFilterTab = {
            key,
            label: RegisteredEmployeeOverviewFilterLabels?.[key],
            count
        };

        return result;
    });
});

export const allPlatformLicenses = createSelector(
    _platformLicenses,
    platformLicensePricingIdsThatHaveNotBeenInvoiced,
    platformLicenseActivationsByPlatformLicense,
    companyIdToNameMap,
    companyIdToCompanyNumberMap,
    centralRegulatorIdToNameMap,
    centralRegulatorIdToCentralRegulatorNumberMap,
    (
        licenses,
        pricingsThatHaveNotBeenInvoiced,
        licenseActivationsByLicense,
        companyNameMap,
        companyNumberMap,
        centralRegulatorNameMap,
        centralRegulatorNumberMap
    ) =>
        buildPlatformLicenseEntities(
            licenses,
            pricingsThatHaveNotBeenInvoiced,
            licenseActivationsByLicense,
            companyNameMap,
            companyNumberMap,
            centralRegulatorNameMap,
            centralRegulatorNumberMap
        )
);

export const selectedBusinessPartner = createSelector(
    selectedCompany,
    selectedRegulator,
    (company, centralRegulator): CompanyDTO | CentralRegulatorDTO => {
        if (company) {
            return company;
        } else if (centralRegulator) {
            return centralRegulator;
        }
        return undefined;
    }
);
export const platformLicenseActivationsOfSelectedBusinessPartner = createSelector(
    selectedBusinessPartner,
    licenseActivationIdsOfCompanyThatHaveNotBeenInvoiced,
    licenseActivationIdsOfCentralRegulatorThatHaveNotBeenInvoiced,
    allPlatformLicenses,
    (
        businessPartner,
        activationsOfCompanyThatHaveNotBeenInvoiced = [],
        activationsOfCentralRegulatorThatHaveNotBeenInvoiced = [],
        platformLicenses
    ) =>
        buildPlatformLicenseActivationWithLicenseEntities(
            businessPartner,
            [...activationsOfCompanyThatHaveNotBeenInvoiced, ...activationsOfCentralRegulatorThatHaveNotBeenInvoiced],
            platformLicenses
        )
);

export const selectedPlatformLicenseId = createSelector(selectAllRouteParams, ({ platformLicenseId }) => platformLicenseId);

export const selectedPlatformLicense = createSelector(
    allPlatformLicenses,
    selectedPlatformLicenseId,
    (platformLicenses, platformLicenseId) => platformLicenses?.find((license) => license.platformLicenseId === platformLicenseId)
);
export const platformLicenseById = (platformLicenseId: string) =>
    createSelector(allPlatformLicenses, (platformLicenses) =>
        platformLicenses?.find((license) => license.platformLicenseId === platformLicenseId)
    );

export const allCompanyProductSelectionsWithMeta = createSelector(
    allCompanyProductSelections,
    (selections): CompanyProductSelectionWithMeta[] =>
        selections?.map((selection) => {
            const attributesThatAreNotOverwritten = selection?.attributes?.filter((attr) => !attr?.overwritten);

            return {
                ...selection,
                name: getAttributeValueInDefaultLanguage({ attributes: attributesThatAreNotOverwritten }, ProductAttribute.NAME),
                overridesPricingOptions: selection?.pricingOptions?.some((pricingOption) => pricingOption?.override),
                overridesAttributes: selection?.attributes?.some((attr) => attr?.override)
            };
        })
);
export const companyProductSelectionsWithMetaBySelectedRegistrationPeriod = createSelector(
    allCompanyProductSelectionsWithMeta,
    selectedRegistrationPeriod,
    (selections, registrationPeriod) =>
        selections?.filter((selection) => registrationPeriod?.companyProductSelectionIds?.includes(selection?.companyProductSelectionId))
);

export const companyProductSelectionsWithMetaBySelectedRegistrationPeriodSortedByPriority = createSelector(
    companyProductSelectionsWithMetaBySelectedRegistrationPeriod,
    (productSelections) =>
        [...productSelections].sort((item1, item2) => {
            const item1Priority = getAttributeFirstValue(item1, ProductAttribute.SORTING_PRIORITY) ?? 0;
            const item2Priority = getAttributeFirstValue(item2, ProductAttribute.SORTING_PRIORITY) ?? 0;
            return item1Priority - item2Priority;
        })
);

export const companyProductSelectionWithMetaById = (companyProductSelectionId) =>
    createSelector(allCompanyProductSelectionsWithMeta, (allSelectionsWithMeta) =>
        allSelectionsWithMeta?.find((cps) => cps?.companyProductSelectionId === companyProductSelectionId)
    );

export const platformLicenseActivationInSelectedBusinessPartnerById = (platformLicenseActivationId: string) =>
    createSelector(selectedBusinessPartner, (businessPartner) =>
        businessPartner?.platformLicenseActivations?.find(
            (licenseActivation) => licenseActivation.platformLicenseActivationId === platformLicenseActivationId
        )
    );

export const allAuditInvoiceSummaries = createSelector(
    _auditInvoiceSummaries,
    allCentralRegulators,
    companies,
    (auditInvoiceSummaries, centralRegulators, allCompanies) =>
        buildAuditInvoiceSummaryEntities(auditInvoiceSummaries, centralRegulators, allCompanies)
);
export const auditInvoiceSummaryById = (auditInvoiceSummaryId: string) =>
    createSelector(allAuditInvoiceSummaries, (allSummaries) =>
        allSummaries?.find((summary) => summary.auditInvoiceSummaryId === auditInvoiceSummaryId)
    );

export const allPayrollReportSummaries = createSelector(_payrollReportSummaries, companies, (payrollReportSummaries, allCompanies) =>
    buildPayrollReportSummaryEntities(payrollReportSummaries, allCompanies)
);
export const payrollReportSummaryById = (id: string) =>
    createSelector(allPayrollReportSummaries, (allSummaries) => allSummaries?.find((summary) => summary.id === id));
